// The client's "has shipped" email, opened for review when tracking is added
// (Stephanie 2026-11-04: "want the 'has shipped' email to open for you to
// review and send when you add tracking, using the same send window as
// invoices" — yes). The same send window: the people on the PO's tracking
// list ticked, the whole email editable, the subject, "Send me a copy".
// Nothing goes until Send. Production always gets a BCC copy (server side).
// Out for delivery and delivered still go automatically.
import { useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { SendToCustomerModal, type PresetRecipient } from "@/components/send/SendToCustomerModal";

type Held = { heldAt: string; heldByName: string | null; warnAt: string; warnedAt: string | null; sentAt: string | null; cancelledAt: string | null; cancelReason: string | null; waiting: boolean };
type Compose = {
  recipients: Array<{ email: string; name: string | null; firstName: string | null; staff: boolean }>;
  sent_at: string | null;
  held?: Held | null;
  held_ready?: boolean;
};

const at = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });

export function ShippedEmailWindow({ shipmentId, onClose }: { shipmentId: string; onClose: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const composeUrl = `/shipments/${shipmentId}/send-compose`;
  // Same cache entry the window reads, so the email is fetched once.
  const q = useQuery({
    queryKey: ["send-compose", composeUrl],
    queryFn: () => api<Compose>(composeUrl),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  if (q.isLoading) return null;
  if (q.isError || !q.data) {
    return createPortal(
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true">
        <div className="max-w-sm rounded-md bg-white p-5 text-sm text-ink shadow-lg">
          <p>Tracking saved, but the client's email couldn't be opened. Nothing was sent.</p>
          <button type="button" onClick={onClose} className="mt-3 rounded-md bg-ink px-3 py-1.5 text-white">Close</button>
        </div>
      </div>,
      document.body,
    );
  }

  const presets: PresetRecipient[] = q.data.recipients.map((r) => ({
    email: r.email,
    ...(r.name ? { name: r.name } : {}),
    label: r.name ?? r.email,
    ...(r.staff ? { sublabel: "Our team — gets a copy (BCC)" } : {}),
    greeting: r.staff ? null : { firstName: r.firstName, name: r.name },
  }));
  const held = q.data.held?.waiting ? q.data.held : null;
  // "Send when it's moving" (Stephanie 2026-11-04) — waiting emails can be
  // cancelled here ("if something happens with the package").
  const cancelHeld = async (): Promise<void> => {
    setCancelling(true);
    setError(null);
    try {
      await api(`/shipments/${shipmentId}/held-email/cancel`, { method: "POST", body: "{}" });
      void qc.invalidateQueries({ queryKey: ["production", "jobs"] });
      await q.refetch();
    } catch {
      setError("It didn't cancel. Try again in a minute.");
    } finally {
      setCancelling(false);
    }
  };
  const notice = held ? (
    <span className="flex flex-wrap items-center justify-between gap-2">
      <span>
        Waiting to send — this email goes to the client once the carrier picks the package up
        {held.heldByName ? ` (set by ${held.heldByName} ${at(held.heldAt)})` : ` (set ${at(held.heldAt)})`}. Production is warned at 8am the next
        morning if it still isn't moving. Send now to send it straight away.
      </span>
      <button type="button" onClick={() => void cancelHeld()} disabled={cancelling} className="font-semibold text-rust-600 underline hover:text-rust-500 disabled:opacity-50">
        {cancelling ? "Cancelling…" : "Cancel the waiting email"}
      </button>
    </span>
  ) : q.data.sent_at ? (
    "This shipment's “has shipped” email already went — Send emails it again."
  ) : null;
  const close = () => {
    void qc.removeQueries({ queryKey: ["send-compose", composeUrl] });
    onClose();
  };

  // Drawn on the page itself, not inside the report's table cell — inside the
  // cell it picked up the cell's centred text (Stephanie 2026-11-04: "why does
  // the screenshot look like that and not like when we send quotes or invoices").
  return createPortal(
    <SendToCustomerModal
      title="Send the “has shipped” email"
      subtitle="Review the client's tracking email, then send. Production gets a copy."
      clientId=""
      presetRecipients={presets}
      contactsLabel="Tracking list"
      ctaLabel={held ? "Send now" : "Send"}
      secondaryCta={{
        label: held ? "Update waiting email" : "Send when it's moving",
        title: q.data.held_ready
          ? "Keeps this email until the carrier picks the package up, then sends it as it is here."
          : "Switches on once Jake runs one database command (it's in the work log).",
        disabled: q.data.held_ready !== true,
      }}
      submitting={sending}
      errorMessage={error}
      noticeMessage={notice}
      showBodyEditor
      composeUrl={composeUrl}
      bodyLabel="Email to the client"
      linkNoun="tracking link"
      currentUserEmail={user?.email ?? null}
      editableSubject
      showCopyToMe
      onCancel={() => {
        if (!sending) close();
      }}
      onSubmit={async (recipients, options) => {
        const to = recipients.filter((r): r is { email: string; name?: string } => "email" in r);
        if (to.length === 0) {
          setError("Pick at least one person to send it to.");
          return;
        }
        setSending(true);
        setError(null);
        try {
          await api(`/shipments/${shipmentId}/send-shipped`, {
            method: "POST",
            body: JSON.stringify({
              recipients: to,
              body_html: options?.bodyHtml ?? "",
              subject: options?.subject ?? "",
              send_copy_to_me: options?.sendCopyToMe === true,
              ...(options?.bcc && options.bcc.length > 0 ? { bcc: options.bcc } : {}),
              ...(options?.secondary ? { when_moving: true } : {}),
            }),
          });
          // The PO can now leave In Production (Stephanie 2026-11-04).
          void qc.invalidateQueries({ queryKey: ["production", "jobs"] });
          close();
        } catch (e) {
          const b = e instanceof ApiError ? (e.body as { error?: string } | null) : null;
          setError(b?.error ?? "The email didn't go. Try again in a minute.");
        } finally {
          setSending(false);
        }
      }}
    />,
    document.body,
  );
}
